import { Box, Button, SvgIcon } from '@mui/material';
import Typography from '@mui/material/Typography';
import DownloadIcon from 'assets/icons/download_icon.svg';
import Next from 'assets/icons/next.svg';
import QrCodeIcon from 'assets/icons/qrCode_icon.svg';
import AppleDownload from 'assets/images/apple_download.svg';
import GoogleDownload from 'assets/images/google_download.svg';
import brandedLogos from 'assets/logos';
import android from 'is-android';
import iOS from 'is-ios';
import React, { useEffect, useMemo, useState } from 'react';
import { I18n, Translate } from 'utils/i18n';
import { Link, useLocation } from 'react-router';
import Invite from 'services/api/Invite';
import {
  buildInviteRedirectUrl,
  getInviteAppConfig,
  resolveInviteAppBrand,
} from 'utils/inviteAppConfig';

const INVITE_LOGO_SRC = {
  default: '/ommnio.svg',
  'saint-gobain': brandedLogos['saint-gobain'],
};

const CUSTOM_SCHEME = 'ommnio://';

const visuallyHiddenSx = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  m: '-1px',
  overflow: 'hidden',
  p: 0,
  position: 'absolute',
  width: '1px',
  whiteSpace: 'nowrap',
};

const InviteScene = () => {
  const location = useLocation();
  const token = useMemo(() => location.pathname.replace('/invite/', '').trim(), [location]);
  const [company, setCompany] = useState(null);
  const [brandReady, setBrandReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (token) {
          const data = await Invite.check(token);
          if (!cancelled) {
            setCompany(data?.company || null);
          }
        }
      } catch {
        // Fall back to hostname-based brand if invite lookup fails.
      } finally {
        if (!cancelled) {
          setBrandReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const brandOpts = useMemo(
    () => ({
      hostname: typeof window !== 'undefined' ? window.location.hostname : '',
      company,
    }),
    [company],
  );

  const brand = useMemo(() => resolveInviteAppBrand(brandOpts), [brandOpts]);
  const appConfig = useMemo(() => getInviteAppConfig(brandOpts), [brandOpts]);
  const logoSrc = INVITE_LOGO_SRC[brand] || INVITE_LOGO_SRC.default;
  // Hostname can already identify Connecti@ (e.g. saint-gobain.web.ommnio.com).
  // Only apex hosts need Invite.check before showing brand chrome / redirecting.
  const brandKnown = brandReady || brand !== 'default';

  const customSchemeURL = useMemo(
    () =>
      `${CUSTOM_SCHEME}invite?token=${encodeURIComponent(token)}&host=${encodeURIComponent(
        window.location.hostname,
      )}`,
    [token],
  );

  const redirectURL = useMemo(
    () =>
      buildInviteRedirectUrl({
        isIOS: iOS,
        isAndroid: android,
        appConfig,
        token,
        customSchemeURL,
      }),
    [customSchemeURL, appConfig, token],
  );

  useEffect(() => {
    if (!brandKnown || !redirectURL || !(iOS || android)) {
      return undefined;
    }
    const redirectTimer = setTimeout(() => {
      window.location.href = redirectURL;
    }, 2000);

    return () => clearTimeout(redirectTimer);
  }, [brandKnown, redirectURL]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        maxWidth: 500,
        margin: '0 auto',
        minHeight: '100vh',
        padding: '20px 20px',
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: '67px',
        }}
      >
        {brandKnown ? (
          <Box
            component="img"
            src={logoSrc}
            alt={appConfig.APP_NAME}
            data-testid="invite-logo"
            sx={{
              display: 'block',
              height: 52,
              filter: theme =>
                theme.palette.mode === 'dark' ? 'invert(1) hue-rotate(180deg)' : 'invert(0)',
            }}
          />
        ) : null}
      </Box>
      {iOS || android ? (
        <Box
          component="h3"
          sx={{
            fontSize: 28,
            fontWeight: 'bold',
            color: 'primary.ultraDark',
            letterSpacing: -0.7,
            mt: 0,
            mb: '21px',
          }}
        >
          {brandKnown ? <Translate value="InviteScene.Redirecting to" /> : null}
        </Box>
      ) : (
        <Box component="section">
          <Typography sx={{ m: '1em 0 0.5em 0' }} gutterBottom variant="h5">
            {I18n.t('InviteScene.Title')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#8b98ab',
              fontWeight: 'normal',
              mb: '2em',
            }}
          >
            {I18n.t('InviteScene.Description')}
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {(android || iOS) && brandKnown ? (
          <Box
            component="a"
            href={redirectURL || undefined}
            aria-label={I18n.t('InviteScene.Install the app')}
            data-testid="invite-store-link"
            sx={{ display: 'block' }}
          >
            {android ? (
              <GoogleDownload
                aria-hidden="true"
                style={{ display: 'block', height: 42, width: '100%' }}
              />
            ) : (
              <AppleDownload
                aria-hidden="true"
                style={{ display: 'block', height: 42, width: '100%' }}
              />
            )}
            <Box component="span" sx={visuallyHiddenSx}>
              {I18n.t('InviteScene.Install the app')}
            </Box>
          </Box>
        ) : null}
      </Box>
      {!iOS && !android ? (
        <Box component={Link} to={`/accept-invite/${token}`} sx={{ placeSelf: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            sx={{
              p: '0 3em',
              mb: '4em',
              '& *': {
                verticalAlign: 'center',
              },
              '& :first-child': {
                mr: '0.25em',
              },
            }}
          >
            <span>
              <Translate value="InviteScene.Button Text" />
            </span>
            <SvgIcon sx={{ width: 16, height: 16 }}>
              <Next />
            </SvgIcon>
          </Button>
        </Box>
      ) : null}
      {iOS || android ? (
        <Box>
          <Box
            component="p"
            sx={{
              fontSize: 20,
              m: 0,
              mb: '14px',
              fontWeight: 'bold',
              lineHeight: 1.3,
              letterSpacing: -0.5,
              color: 'primary.ultraDark',
            }}
          >
            <Translate value="InviteScene.Whats next" />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: 160,
                height: 160,
                padding: '38px 20px 0',
                bgcolor: 'secondary.ultraLight',
                border: '1px solid transparent',
                borderRadius: '8px',
              }}
            >
              <DownloadIcon style={{ display: 'block', width: 32, height: 32, marginBottom: 14 }} />
              <Box
                component="p"
                sx={{
                  fontSize: 15,
                  lineHeight: 1.47,
                  letterSpacing: -0.5,
                  textAlign: 'center',
                  color: theme => theme.palette.black,
                  m: 0,
                }}
              >
                1.
                <Translate value="InviteScene.Install the app" />
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: 160,
                height: 160,
                padding: '38px 20px 0',
                bgcolor: 'secondary.ultraLight',
                border: '1px solid transparent',
                borderRadius: '8px',
              }}
            >
              <QrCodeIcon style={{ display: 'block', width: 32, height: 32, marginBottom: 14 }} />
              <Box
                component="p"
                sx={{
                  fontSize: 15,
                  lineHeight: 1.47,
                  letterSpacing: -0.5,
                  textAlign: 'center',
                  color: theme => theme.palette.black,
                  m: 0,
                }}
              >
                2.
                <Translate value="InviteScene.Scan QR" />
              </Box>
            </Box>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default InviteScene;
